import { EnvironmentProviders, ErrorHandler, Provider, effect, inject, provideAppInitializer } from '@angular/core';
import * as Sentry from '@sentry/angular';
import { User } from '../models/user.model';
import { AuthService } from '../services/auth.service';

export interface SentryConfig {
  dsn: string;
  enabled: boolean;
  environment: string;
}

/** SDK surface used by the app. Tests pass a fake instead of mocking the module. */
export type SentrySdk = Pick<typeof Sentry, 'init' | 'createErrorHandler' | 'setUser' | 'replayIntegration'>;

// Named functions (not the whole namespace) so unused SDK features are tree-shaken.
const defaultSdk: SentrySdk = {
  init: Sentry.init,
  createErrorHandler: Sentry.createErrorHandler,
  setUser: Sentry.setUser,
  replayIntegration: Sentry.replayIntegration,
};

/**
 * Initializes Sentry error monitoring. Must run before bootstrapApplication.
 * No-op when Sentry is disabled or the DSN is missing (e.g. local).
 */
export function initSentry(config: SentryConfig, sdk: SentrySdk = defaultSdk): boolean {
  if (!config.enabled || !config.dsn) return false;

  sdk.init({
    dsn: config.dsn,
    environment: config.environment,
    // Session replay only for sessions with an error; no tracing (SPA-26410).
    // All text, inputs and media are masked: the app shows clinical data.
    integrations: [sdk.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true })],
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
  return true;
}

/**
 * Replaces Angular's ErrorHandler so uncaught errors are reported to Sentry.
 * Errors are still logged to the console; when Sentry is not initialized, reporting is a no-op.
 */
export function provideSentryErrorHandler(sdk: SentrySdk = defaultSdk): Provider {
  return { provide: ErrorHandler, useValue: sdk.createErrorHandler({ logErrors: true }) };
}

/** Syncs the signed-in user with Sentry; pass null on logout to clear it. */
export function setSentryUser(
  user: Pick<User, 'email' | 'role'> | null,
  sdk: SentrySdk = defaultSdk
): void {
  // No id: AuthService only has a placeholder ("temporary-user"); email identifies the user.
  sdk.setUser(user ? { email: user.email, role: user.role } : null);
}

/** Keeps the Sentry user in sync with the AuthService session (login, role switch, logout). */
export function provideSentryUserSync(sdk: SentrySdk = defaultSdk): EnvironmentProviders {
  return provideAppInitializer(() => {
    const authService = inject(AuthService);
    effect(() => setSentryUser(authService.currentUser(), sdk));
  });
}
